import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { provinceConversionApi } from '../../../../services/provinceConversionApi';
import type { ConversionProgress, CreateConversionInput } from '../../../../schemas/provinceConversion';

const LIST_QUERY_KEY = ['province-conversions'] as const;
const BOUNDARIES_QUERY_KEY = ['province-conversions', 'current-boundaries'] as const;

export function useConversionBatchesQuery() {
	return useQuery({
		queryKey: LIST_QUERY_KEY,
		queryFn: provinceConversionApi.list,
	});
}

export function useCreateConversionBatchMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: CreateConversionInput) => provinceConversionApi.create(input),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY }),
	});
}

export function useUploadMappingsMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ dotId, file }: { dotId: string; file: File }) => provinceConversionApi.uploadMappings(dotId, file),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY }),
	});
}

/**
 * Preview là POST (phép tính nặng phía server) — cố tình cài đặt bằng
 * `useMutation`, không phải `useQuery`, để TanStack Query KHÔNG BAO GIỜ tự
 * refetch-on-focus; admin phải chủ động bấm "Xem trước" lại khi muốn làm mới.
 */
export function usePreviewConversionMutation() {
	return useMutation({
		mutationFn: (dotId: string) => provinceConversionApi.preview(dotId),
	});
}

export interface ConversionActionInput {
	dotId: string;
	onProgress?: (progress: ConversionProgress) => void;
}

export function useApplyConversionMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ dotId, onProgress }: ConversionActionInput) => provinceConversionApi.apply(dotId, onProgress),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY }),
	});
}

export function useRevertConversionMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ dotId, onProgress }: ConversionActionInput) => provinceConversionApi.revert(dotId, onProgress),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY }),
	});
}

export function useDeleteConversionMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (dotId: string) => provinceConversionApi.remove(dotId),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY }),
	});
}

/** Ánh xạ chi tiết của một đợt — chỉ tải khi có đợt đang được chọn. */
export function useConversionBatchMappingsQuery(dotId: string | null) {
	return useQuery({
		queryKey: [...LIST_QUERY_KEY, dotId, 'mappings'],
		queryFn: () => provinceConversionApi.getMappings(dotId as string),
		enabled: Boolean(dotId),
	});
}

/** Nền cho `ConversionPreviewMap` — chỉ tải khi thật sự cần (đã có kết quả preview). */
export function useCurrentBoundariesQuery(enabled: boolean) {
	return useQuery({
		queryKey: BOUNDARIES_QUERY_KEY,
		queryFn: provinceConversionApi.getCurrentBoundaries,
		enabled,
		staleTime: 5 * 60 * 1000,
	});
}
